import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const API_BASE = import.meta?.env?.VITE_API_URL || "https://bmtvendorvendorfinal.onrender.com";
const BASE = `${API_BASE}/api/cabs/vendor`;

const getToken = () => {
  const v = localStorage.getItem("vendorToken");
  return v && v !== "null" && v !== "undefined" ? v : "";
};

async function api(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(options.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    if (res.status === 404 && /route not found/i.test(data.message || "")) {
      throw new Error("Cab bookings API server par mount nahi hai. server.js aur routes file check karo.");
    }
    throw new Error(
      data.message ||
        (res.status === 401 ? "Session expired. Please log in again." : "Something went wrong")
    );
  }
  return data;
}

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const pickupDateOf = (b) => {
  const d = new Date(b?.pickupDate);
  return isNaN(d) ? null : d;
};

const isFutureTrip = (b) => {
  const d = pickupDateOf(b);
  return !!d && d >= startOfToday();
};

const ACTIVE = ["PENDING", "CONFIRMED", "DRIVER_ASSIGNED", "STARTED"];

const TABS = [
  {
    key: "upcoming",
    label: "Upcoming",
    hint: "Trips yet to happen",
    test: (b) => ACTIVE.includes(b.bookingStatus) && (b.bookingStatus === "STARTED" || isFutureTrip(b)),
    empty: "No upcoming trips.",
  },
  {
    key: "pending",
    label: "Pending",
    hint: "Waiting for confirmation",
    test: (b) => b.bookingStatus === "PENDING",
    empty: "No pending bookings.",
  },
  {
    key: "confirmed",
    label: "Confirmed",
    hint: "Accepted or on the road",
    test: (b) => ["CONFIRMED", "DRIVER_ASSIGNED", "STARTED"].includes(b.bookingStatus),
    empty: "No confirmed bookings yet.",
  },
  {
    key: "completed",
    label: "Completed",
    hint: "Finished trips",
    test: (b) => b.bookingStatus === "COMPLETED",
    empty: "No completed trips yet.",
  },
  {
    key: "cancelled",
    label: "Cancelled",
    hint: "Cancelled by guest or you",
    test: (b) => b.bookingStatus === "CANCELLED",
    empty: "No cancelled bookings.",
  },
];

const money = (n, currency = "INR") => {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
      maximumFractionDigits: 0,
    }).format(Number(n) || 0);
  } catch {
    return `${currency || "INR"} ${Number(n) || 0}`;
  }
};

const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d)
    ? "—"
    : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const fmtDateTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d)
    ? "—"
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
};

const cap = (s = "") => String(s).charAt(0).toUpperCase() + String(s).slice(1);
const label = (s = "") => cap(String(s).toLowerCase().replace(/_/g, " "));
const cls = (s = "") => String(s).toLowerCase();

const canCancel = (b) => ["PENDING", "CONFIRMED", "DRIVER_ASSIGNED", "STARTED"].includes(b?.bookingStatus);
const canDelete = (b) =>
  b?.bookingStatus === "CANCELLED" ||
  (b?.bookingStatus === "PENDING" && b?.paymentStatus !== "PAID");
const canAssign = (b) => ["CONFIRMED", "DRIVER_ASSIGNED"].includes(b?.bookingStatus);

const totalOf = (b) => b?.pricing?.totalAmount || 0;
const currencyOf = (b) => b?.pricing?.currency || "INR";
const routeOf = (b) => `${b.fromCity || "Pickup"} → ${b.toCity || "Drop"}`;

export default function VendorCabBookings() {
  const rootRef = useRef(null);

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [tab, setTab] = useState("upcoming");
  const [search, setSearch] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [tripFilter, setTripFilter] = useState("");

  const [detailId, setDetailId] = useState(null);
  const [busy, setBusy] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [driver, setDriver] = useState({ name: "", phone: "", vehicleNumber: "" });

  useEffect(() => {
    rootRef.current?.scrollIntoView({ block: "start" });
  }, [detailId]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(""), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const d = await api(`${BASE}/my-bookings`);
      setBookings(Array.isArray(d.data) ? d.data : []);
    } catch (e) {
      setError(e.message);
      setBookings([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c = {};
    TABS.forEach((t) => {
      c[t.key] = bookings.filter(t.test).length;
    });
    return c;
  }, [bookings]);

  const stats = useMemo(() => {
    const live = bookings.filter((b) => b.bookingStatus !== "CANCELLED");
    return {
      revenue: live.filter((b) => b.paymentStatus === "PAID").reduce((s, b) => s + totalOf(b), 0),
      awaiting: live.filter((b) => b.paymentStatus !== "PAID").reduce((s, b) => s + totalOf(b), 0),
      trips: bookings.filter(
        (b) => ["CONFIRMED", "DRIVER_ASSIGNED"].includes(b.bookingStatus) && isFutureTrip(b)
      ).length,
      currency: currencyOf(bookings[0] || {}),
    };
  }, [bookings]);

  const activeTab = TABS.find((t) => t.key === tab);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = bookings.filter((b) => {
      if (!activeTab.test(b)) return false;
      if (paymentFilter && b.paymentStatus !== paymentFilter) return false;
      if (tripFilter && b.tripType !== tripFilter) return false;
      if (!q) return true;
      return [
        b.bookingId,
        b.customer?.name,
        b.customer?.email,
        b.customer?.phone,
        b.fromCity,
        b.toCity,
        b.cab?.cabType,
        b.cab?.vehicleNumber,
        b.driverDetails?.name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
    if (tab === "upcoming") {
      list.sort((a, b) => (pickupDateOf(a) || 0) - (pickupDateOf(b) || 0));
    }
    return list;
  }, [bookings, activeTab, tab, search, paymentFilter, tripFilter]);

  const detail = detailId ? bookings.find((b) => b._id === detailId) : null;

  const openDetail = (b) => {
    setCancelReason("");
    setDriver({
      name: b.driverDetails?.name || "",
      phone: b.driverDetails?.phone || "",
      vehicleNumber: b.driverDetails?.vehicleNumber || b.cab?.vehicleNumber || "",
    });
    setDetailId(b._id);
  };

  const closeDetail = () => {
    if (busy) return;
    setDetailId(null);
    setCancelReason("");
  };

  const replaceBooking = (updated) => {
    setBookings((prev) => prev.map((x) => (x._id === updated._id ? updated : x)));
  };

  const act = async (b, action) => {
    const confirmText = {
      confirm:
        b.paymentStatus === "PAID"
          ? "Confirm this booking?"
          : "Payment is not received yet. Confirm this booking anyway?",
      "assign-driver": `Assign ${driver.name || "this driver"} to this trip?`,
      "start-trip": "Start this trip?",
      "complete-trip": "Mark this trip as completed?",
      cancel:
        b.paymentStatus === "PAID"
          ? "Payment is already received. Cancel this booking? You will need to process the refund."
          : "Cancel this booking?",
    };

    if (action === "assign-driver" && (!driver.name.trim() || !driver.phone.trim())) {
      setError("Driver name and phone are required.");
      return;
    }
    if (!window.confirm(confirmText[action])) return;

    let body = {};
    if (action === "cancel") body = { reason: cancelReason };
    if (action === "assign-driver") body = driver;

    setBusy(`${action}:${b._id}`);
    setError("");
    setNotice("");
    try {
      const d = await api(`${BASE}/${b._id}/${action}`, {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      if (d.data) replaceBooking(d.data);
      else await load(true);
      setNotice(d.message || "Booking updated.");
      if (action === "cancel") setCancelReason("");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  const remove = async (b) => {
    if (!window.confirm("Delete this booking permanently? This cannot be undone.")) return;
    setBusy(`delete:${b._id}`);
    setError("");
    setNotice("");
    try {
      const d = await api(`${BASE}/${b._id}`, { method: "DELETE" });
      setBookings((prev) => prev.filter((x) => x._id !== b._id));
      setDetailId(null);
      setNotice(d.message || "Booking deleted.");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  const isBusy = (action, id) => busy === `${action}:${id}`;

  const rowActions = (b, compact) => {
    const c = `btn${compact ? " sm" : ""}`;
    const stop = (fn) => (e) => {
      e.stopPropagation();
      fn();
    };
    return (
      <>
        {b.bookingStatus === "PENDING" && (
          <button className={`${c} green`} disabled={!!busy} onClick={stop(() => act(b, "confirm"))}>
            {isBusy("confirm", b._id) ? "Confirming…" : "Confirm"}
          </button>
        )}
        {["CONFIRMED", "DRIVER_ASSIGNED"].includes(b.bookingStatus) && (
          <button className={`${c} primary`} disabled={!!busy} onClick={stop(() => act(b, "start-trip"))}>
            {isBusy("start-trip", b._id) ? "Starting…" : "Start trip"}
          </button>
        )}
        {b.bookingStatus === "STARTED" && (
          <button className={`${c} green`} disabled={!!busy} onClick={stop(() => act(b, "complete-trip"))}>
            {isBusy("complete-trip", b._id) ? "Saving…" : "Complete trip"}
          </button>
        )}
      </>
    );
  };

  return (
    <div className="neb" ref={rootRef}>
      <style>{CSS}</style>

      {detail ? (
        <BookingDetail
          b={detail}
          busy={busy}
          isBusy={isBusy}
          cancelReason={cancelReason}
          setCancelReason={setCancelReason}
          driver={driver}
          setDriver={setDriver}
          onBack={closeDetail}
          onAct={act}
          onDelete={remove}
          error={error}
          notice={notice}
          clearError={() => setError("")}
          rowActions={rowActions}
        />
      ) : (
        <>
          <header className="neb-head">
            <div>
              <h1>Cab bookings</h1>
              <p>Confirm bookings, assign drivers, run trips and review past rides.</p>
            </div>
            <button className="btn" onClick={() => load(true)} disabled={loading || refreshing}>
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </header>

          {error && (
            <div className="alert bad" role="alert">
              <span>{error}</span>
              <button onClick={() => setError("")} aria-label="Dismiss">×</button>
            </div>
          )}
          {notice && (
            <div className="alert good" role="status">
              <span>{notice}</span>
              <button onClick={() => setNotice("")} aria-label="Dismiss">×</button>
            </div>
          )}

          <section className="neb-stats">
            <Stat label="Revenue (paid)" value={money(stats.revenue, stats.currency)} />
            <Stat label="Awaiting payment" value={money(stats.awaiting, stats.currency)} />
            <Stat label="Confirmed trips ahead" value={stats.trips} />
            <Stat label="Total bookings" value={bookings.length} />
          </section>

          <div className="neb-tabs" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.key}
                role="tab"
                aria-selected={tab === t.key}
                className={`neb-tab ${tab === t.key ? "active" : ""}`}
                onClick={() => setTab(t.key)}
              >
                <span className="neb-tab-text">
                  <strong>{t.label}</strong>
                  <small>{t.hint}</small>
                </span>
                <span className="neb-tab-count">{counts[t.key] || 0}</span>
              </button>
            ))}
          </div>

          <div className="neb-filters">
            <input
              type="search"
              placeholder="Search booking ID, guest, route, cab or driver"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select value={tripFilter} onChange={(e) => setTripFilter(e.target.value)}>
              <option value="">All trip types</option>
              <option value="ONE_WAY">One way</option>
              <option value="ROUND_TRIP">Round trip</option>
              <option value="LOCAL">Local</option>
            </select>
            <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}>
              <option value="">All payments</option>
              <option value="PENDING">Payment pending</option>
              <option value="PAID">Paid</option>
              <option value="FAILED">Failed</option>
              <option value="REFUNDED">Refunded</option>
              <option value="PARTIAL_REFUND">Partial refund</option>
            </select>
          </div>

          <div className="neb-card">
            <div className="neb-card-head">
              <h2>{activeTab.label} bookings</h2>
              <span className="muted">
                {filtered.length} booking{filtered.length === 1 ? "" : "s"}
              </span>
            </div>

            {loading ? (
              <div className="neb-skel">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="skeleton" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="empty">
                <h3>
                  {search || paymentFilter || tripFilter ? "No matching bookings" : activeTab.empty}
                </h3>
                {(search || paymentFilter || tripFilter) && <p>Try clearing the search or filters.</p>}
              </div>
            ) : (
              <div className="neb-table-wrap">
                <table className="neb-table">
                  <thead>
                    <tr>
                      <th>Booking</th>
                      <th>Guest</th>
                      <th>Trip</th>
                      <th>Cab</th>
                      <th>Riders</th>
                      <th>Total</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((b) => (
                      <tr key={b._id} onClick={() => openDetail(b)}>
                        <td>
                          <strong>{b.bookingId || "—"}</strong>
                          <small>Booked {fmtDate(b.createdAt)}</small>
                        </td>
                        <td>
                          <strong>{b.customer?.name || "—"}</strong>
                          <small>{b.customer?.phone || b.customer?.email || "—"}</small>
                        </td>
                        <td>
                          <strong>{routeOf(b)}</strong>
                          <small>
                            {fmtDate(b.pickupDate)}
                            {b.pickupTime ? ` · ${b.pickupTime}` : ""} · {label(b.tripType)}
                          </small>
                        </td>
                        <td>
                          <strong>{b.cab?.cabType || "Cab"}</strong>
                          <small>
                            {b.driverDetails?.name
                              ? `Driver: ${b.driverDetails.name}`
                              : b.cab?.vehicleNumber || "No driver yet"}
                          </small>
                        </td>
                        <td>{b.totalPassengers}</td>
                        <td>
                          <strong>{money(totalOf(b), currencyOf(b))}</strong>
                          <small>
                            <span className={`pill pay-${cls(b.paymentStatus)}`}>{label(b.paymentStatus)}</span>
                          </small>
                        </td>
                        <td>
                          <span className={`pill st-${cls(b.bookingStatus)}`}>{label(b.bookingStatus)}</span>
                        </td>
                        <td>
                          <div className="row-actions">
                            {rowActions(b, true)}
                            {b.bookingStatus === "CANCELLED" && (
                              <button
                                className="btn sm danger-ghost"
                                disabled={!!busy}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  remove(b);
                                }}
                              >
                                Delete
                              </button>
                            )}
                            <button
                              className="btn sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetail(b);
                              }}
                            >
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label: l, value }) {
  return (
    <div className="stat">
      <strong>{value}</strong>
      <span>{l}</span>
    </div>
  );
}

function Info({ label: l, value }) {
  return (
    <div className="info">
      <span>{l}</span>
      <strong>{value || "—"}</strong>
    </div>
  );
}

function BookingDetail({
  b,
  busy,
  isBusy,
  cancelReason,
  setCancelReason,
  driver,
  setDriver,
  onBack,
  onAct,
  onDelete,
  error,
  notice,
  clearError,
  rowActions,
}) {
  const p = b.pricing || {};
  const cab = b.cab || {};
  const setField = (k) => (e) => setDriver((d) => ({ ...d, [k]: e.target.value }));

  return (
    <div className="detail">
      <button type="button" className="back" onClick={onBack} disabled={!!busy}>
        ← Back to bookings
      </button>

      <header className="neb-head">
        <div>
          <h1>{b.bookingId || "Booking"}</h1>
          <div className="badges">
            <span className={`pill st-${cls(b.bookingStatus)}`}>{label(b.bookingStatus)}</span>
            <span className={`pill pay-${cls(b.paymentStatus)}`}>payment {label(b.paymentStatus)}</span>
          </div>
        </div>
        <div className="detail-total">
          <span>Total</span>
          <strong>{money(p.totalAmount, p.currency)}</strong>
        </div>
      </header>

      {error && (
        <div className="alert bad" role="alert">
          <span>{error}</span>
          <button onClick={clearError} aria-label="Dismiss">×</button>
        </div>
      )}
      {notice && (
        <div className="alert good" role="status">
          <span>{notice}</span>
        </div>
      )}

      <div className="detail-grid">
        <section className="panel">
          <h2>Guest</h2>
          <div className="info-grid">
            <Info label="Name" value={b.customer?.name} />
            <Info label="Phone" value={b.customer?.phone} />
            <Info label="Email" value={b.customer?.email} />
            <Info label="Booked on" value={fmtDateTime(b.createdAt)} />
          </div>
        </section>

        <section className="panel">
          <h2>Trip</h2>
          <div className="info-grid">
            <Info label="Trip type" value={label(b.tripType)} />
            <Info label="Route" value={routeOf(b)} />
            <Info label="Pickup" value={`${fmtDate(b.pickupDate)}${b.pickupTime ? ` · ${b.pickupTime}` : ""}`} />
            <Info
              label="Return"
              value={b.tripType === "ROUND_TRIP" ? `${fmtDate(b.returnDate)}${b.returnTime ? ` · ${b.returnTime}` : ""}` : ""}
            />
            <Info label="Pickup address" value={b.pickupAddress} />
            <Info label="Drop address" value={b.dropAddress} />
          </div>
          {b.specialRequests && <p className="note">Request: {b.specialRequests}</p>}
        </section>

        <section className="panel">
          <h2>Cab</h2>
          <div className="info-grid">
            <Info label="Cab type" value={cab.cabType} />
            <Info label="Vehicle number" value={cab.vehicleNumber} />
            <Info label="Riders" value={b.totalPassengers} />
          </div>
          {b.passengers?.length > 0 && (
            <div className="info-grid" style={{ marginTop: 10 }}>
              {b.passengers.map((x, i) => (
                <Info
                  key={i}
                  label={`Passenger ${i + 1}`}
                  value={[x.name, x.age ? `${x.age} yrs` : "", x.gender ? label(x.gender) : ""]
                    .filter(Boolean)
                    .join(" · ")}
                />
              ))}
            </div>
          )}
        </section>

        <section className="panel">
          <h2>Payment</h2>
          <div className="info-grid">
            <Info label="Base fare" value={money(p.baseFare, p.currency)} />
            <Info label="Driver allowance" value={money(p.driverAllowance, p.currency)} />
            <Info label="Toll charges" value={money(p.tollCharges, p.currency)} />
            <Info label="State tax" value={money(p.stateTax, p.currency)} />
            <Info label={`GST (${p.gstPercentage || 0}%)`} value={money(p.gstAmount, p.currency)} />
            <Info label="Discount" value={money(p.discount, p.currency)} />
            <Info label="Total amount" value={money(p.totalAmount, p.currency)} />
            <Info label="Status" value={label(b.paymentStatus)} />
            <Info label="Method" value={label(b.paymentMethod)} />
            <Info label="Paid on" value={fmtDateTime(b.paidAt)} />
            <Info label="Payment ID" value={b.razorpayPaymentId} />
          </div>
        </section>

        <section className="panel">
          <h2>Driver</h2>
          {canAssign(b) ? (
            <div className="info-grid">
              <label className="field">
                <span>Driver name</span>
                <input value={driver.name} onChange={setField("name")} placeholder="Full name" />
              </label>
              <label className="field">
                <span>Driver phone</span>
                <input value={driver.phone} onChange={setField("phone")} placeholder="10-digit number" />
              </label>
              <label className="field">
                <span>Vehicle number</span>
                <input value={driver.vehicleNumber} onChange={setField("vehicleNumber")} placeholder="PB 10 AB 1234" />
              </label>
              <div className="field" style={{ justifyContent: "flex-end" }}>
                <button className="btn primary" disabled={!!busy} onClick={() => onAct(b, "assign-driver")}>
                  {isBusy("assign-driver", b._id)
                    ? "Saving…"
                    : b.bookingStatus === "DRIVER_ASSIGNED"
                    ? "Update driver"
                    : "Assign driver"}
                </button>
              </div>
            </div>
          ) : (
            <div className="info-grid">
              <Info label="Name" value={b.driverDetails?.name} />
              <Info label="Phone" value={b.driverDetails?.phone} />
              <Info label="Vehicle" value={b.driverDetails?.vehicleNumber} />
              <Info label="Assigned at" value={fmtDateTime(b.driverDetails?.assignedAt)} />
            </div>
          )}
          {b.bookingStatus === "PENDING" && <p className="note">Confirm the booking to assign a driver.</p>}
        </section>

        <section className="panel">
          <h2>Timeline</h2>
          <div className="info-grid">
            <Info label="Confirmed on" value={fmtDateTime(b.confirmedAt)} />
            <Info label="Started on" value={fmtDateTime(b.startedAt)} />
            <Info label="Completed on" value={fmtDateTime(b.completedAt)} />
          </div>
        </section>

        {b.bookingStatus === "CANCELLED" && (
          <section className="panel">
            <h2>Cancellation</h2>
            <div className="info-grid">
              <Info label="Cancelled on" value={fmtDateTime(b.cancelledAt)} />
              <Info label="Cancelled by" value={label(b.cancelledBy)} />
              <Info label="Reason" value={b.cancellationReason || "No reason given"} />
            </div>
            {b.paymentStatus === "PAID" && (
              <p className="note warn">
                Payment was received for this booking. Please make sure the refund is processed.
              </p>
            )}
          </section>
        )}

        {canCancel(b) && (
          <section className="panel">
            <h2>Cancel booking</h2>
            <label className="field">
              <span>Reason (optional)</span>
              <textarea
                rows="3"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Why is this booking being cancelled?"
              />
            </label>
          </section>
        )}
      </div>

      <div className="actionbar">
        <button type="button" className="btn" onClick={onBack} disabled={!!busy}>
          Close
        </button>
        <div className="actions">
          {canDelete(b) && (
            <button className="btn danger-ghost" disabled={!!busy} onClick={() => onDelete(b)}>
              {isBusy("delete", b._id) ? "Deleting…" : "Delete booking"}
            </button>
          )}
          {canCancel(b) && (
            <button className="btn danger-ghost" disabled={!!busy} onClick={() => onAct(b, "cancel")}>
              {isBusy("cancel", b._id) ? "Cancelling…" : "Cancel booking"}
            </button>
          )}
          {rowActions(b, false)}
        </div>
      </div>
    </div>
  );
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@500;700&family=Figtree:wght@400;500;600&display=swap');

.neb{
  --bg:#ffffff; --panel:#ffffff; --soft:#f5f4f9; --line:#e4e1ee;
  --text:#1c1830; --muted:#6b657f;
  --accent:#5b3fd9; --accent-dark:#4a31b8; --accent-soft:#ece8fb;
  --good:#1a8a5a; --good-bg:#e5f6ee;
  --warn:#a76a00; --warn-bg:#fff3d9;
  --bad:#c42b46;  --bad-bg:#fdeaee;
  --info:#1d4ed8; --info-bg:#e3ecfd;
  font-family:'Figtree',system-ui,sans-serif; color:var(--text); background:var(--bg);
  min-height:100vh; padding:32px clamp(16px,4vw,48px); box-sizing:border-box;
}
.neb *{box-sizing:border-box}
.neb h1,.neb h2,.neb h3{font-family:'Bricolage Grotesque',system-ui,sans-serif;margin:0}
.neb-head{display:flex;justify-content:space-between;align-items:flex-end;gap:16px;flex-wrap:wrap;margin-bottom:22px}
.neb-head h1{font-size:clamp(26px,4vw,38px);font-weight:700}
.neb-head p{margin:6px 0 0;color:var(--muted)}
.muted{color:var(--muted);font-size:14px}

.neb input,.neb select,.neb textarea{
  background:#fff;border:1px solid var(--line);color:var(--text);border-radius:8px;
  padding:10px 12px;font:inherit;width:100%;
}
.neb input::placeholder,.neb textarea::placeholder{color:#a9a4ba}
.neb input:focus,.neb select:focus,.neb textarea:focus,.neb button:focus-visible{outline:2px solid var(--accent);outline-offset:1px}

.btn{background:#fff;color:var(--text);border:1px solid var(--line);border-radius:8px;padding:10px 16px;font:inherit;font-weight:600;cursor:pointer;white-space:nowrap}
.btn:hover:not(:disabled){border-color:var(--muted)}
.btn:disabled{opacity:.5;cursor:not-allowed}
.btn.sm{padding:6px 12px;font-size:13px}
.btn.primary{background:var(--accent);border-color:var(--accent);color:#fff}
.btn.primary:hover:not(:disabled){background:var(--accent-dark);border-color:var(--accent-dark)}
.btn.green{background:var(--good);border-color:var(--good);color:#fff}
.btn.danger-ghost{color:var(--bad)}
.btn.danger-ghost:hover:not(:disabled){border-color:var(--bad)}

.alert{display:flex;align-items:center;gap:10px;padding:12px 16px;border-radius:10px;font-size:14px;margin-bottom:16px}
.alert span{flex:1}
.alert button{background:none;border:0;font-size:20px;line-height:1;cursor:pointer;color:inherit}
.alert.bad{background:var(--bad-bg);color:var(--bad)}
.alert.good{background:var(--good-bg);color:var(--good)}

.neb-stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:12px;margin-bottom:18px}
.stat{background:var(--soft);border-radius:10px;padding:14px 16px;display:flex;flex-direction:column;gap:2px}
.stat strong{font-family:'Bricolage Grotesque',sans-serif;font-size:26px;font-weight:700}
.stat span{color:var(--muted);font-size:13px}

.neb-tabs{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:10px;margin-bottom:16px}
@media(max-width:1100px){.neb-tabs{grid-template-columns:repeat(3,minmax(0,1fr))}}
@media(max-width:640px){.neb-tabs{grid-template-columns:repeat(2,minmax(0,1fr))}}
.neb-tab{display:flex;align-items:center;justify-content:space-between;gap:10px;text-align:left;padding:12px 14px;background:#fff;border:1.5px solid var(--line);border-radius:12px;cursor:pointer;font:inherit;color:var(--text);transition:border-color .15s,box-shadow .15s}
.neb-tab:hover{box-shadow:0 4px 14px rgba(28,24,48,.08)}
.neb-tab.active{border-color:var(--accent);box-shadow:0 0 0 3px var(--accent-soft)}
.neb-tab-text{display:flex;flex-direction:column;min-width:0}
.neb-tab-text strong{font-size:15px}
.neb-tab-text small{font-size:12px;color:var(--muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.neb-tab-count{font-family:'Bricolage Grotesque',sans-serif;font-size:24px;font-weight:700}
.neb-tab.active .neb-tab-count{color:var(--accent)}

.neb-filters{display:grid;grid-template-columns:2fr 1.4fr 1fr;gap:10px;margin-bottom:16px}
@media(max-width:800px){.neb-filters{grid-template-columns:1fr}}

.neb-card{background:#fff;border:1px solid var(--line);border-radius:12px;overflow:hidden}
.neb-card-head{display:flex;justify-content:space-between;align-items:center;padding:16px 20px;border-bottom:1px solid var(--line)}
.neb-card-head h2{font-size:18px}
.neb-table-wrap{overflow-x:auto}
.neb-table{width:100%;min-width:1000px;border-collapse:collapse}
.neb-table th{text-align:left;font-size:12px;font-weight:600;letter-spacing:.04em;text-transform:uppercase;color:var(--muted);padding:11px 16px;background:var(--soft);white-space:nowrap}
.neb-table td{padding:13px 16px;border-top:1px solid var(--line);font-size:14px;vertical-align:middle}
.neb-table tbody tr{cursor:pointer;transition:background .12s}
.neb-table tbody tr:hover{background:#faf9fd}
.neb-table td strong{display:block;font-weight:600}
.neb-table td small{display:block;margin-top:3px;font-size:12px;color:var(--muted)}
.row-actions{display:flex;gap:8px;flex-wrap:wrap}

.pill{display:inline-block;padding:3px 10px;border-radius:99px;font-size:12px;font-weight:600;text-transform:capitalize;background:var(--soft);color:var(--muted);white-space:nowrap}
.pill.st-confirmed,.pill.pay-paid{background:var(--good-bg);color:var(--good)}
.pill.st-pending,.pill.pay-pending{background:var(--warn-bg);color:var(--warn)}
.pill.st-cancelled,.pill.pay-failed{background:var(--bad-bg);color:var(--bad)}
.pill.st-completed,.pill.pay-refunded,.pill.pay-partial_refund{background:var(--info-bg);color:var(--info)}
.pill.st-driver_assigned,.pill.st-started{background:var(--accent-soft);color:var(--accent-dark)}

.empty{text-align:center;padding:56px 20px;color:var(--muted)}
.empty h3{font-size:19px;color:var(--text)}
.empty p{margin:8px 0 0}
.neb-skel{display:grid;gap:10px;padding:20px}
.skeleton{height:54px;border-radius:10px;background:linear-gradient(90deg,var(--soft),#fff,var(--soft));background-size:200% 100%;animation:nebsh 1.4s infinite}
@keyframes nebsh{to{background-position:-200% 0}}
@media(prefers-reduced-motion:reduce){.skeleton{animation:none}}

.detail{max-width:1100px;margin:0 auto}
.back{background:none;border:0;padding:0;margin-bottom:14px;color:var(--accent);font:inherit;font-weight:600;cursor:pointer}
.back:hover{text-decoration:underline}
.badges{display:flex;gap:8px;flex-wrap:wrap;margin-top:10px}
.detail-total{text-align:right;display:flex;flex-direction:column}
.detail-total span{color:var(--muted);font-size:13px}
.detail-total strong{font-family:'Bricolage Grotesque',sans-serif;font-size:32px}
.detail-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px}
@media(max-width:800px){.detail-grid{grid-template-columns:1fr}}
.panel{background:#fff;border:1px solid var(--line);border-radius:12px;padding:20px}
.panel h2{font-size:17px;margin-bottom:14px}
.info-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}
@media(max-width:480px){.info-grid{grid-template-columns:1fr}}
.info{background:var(--soft);border-radius:10px;padding:10px 14px;display:flex;flex-direction:column;gap:2px;min-width:0}
.info span{font-size:12px;color:var(--muted)}
.info strong{font-size:14px;word-break:break-word}
.note{margin:12px 0 0;font-size:14px;border-radius:8px;padding:10px 12px;background:var(--soft);color:var(--muted)}
.note.warn{background:var(--warn-bg);color:var(--warn)}
.field{display:flex;flex-direction:column;gap:6px}
.field>span{font-size:13px;color:var(--muted)}

.actionbar{position:sticky;bottom:0;z-index:5;margin-top:20px;background:#fff;border-top:1px solid var(--line);padding:14px 0;display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap}
.actions{display:flex;gap:10px;flex-wrap:wrap}
`;